import { useLingui } from '@lingui/react/macro';
import { Box, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useEffect, useState } from 'react';
import { hoverMenuItemTabIndex } from '../hover_menu.tsx';
import {
  NavMenuItemButton,
  navMenuItemDescriptionSx,
  navMenuItemHeaderSx,
  navMenuItemIconSx,
} from '../nav_hover_group/nav_menu_item_styles.ts';
import { REWARDS_MENU_DETAILS_ENABLED } from './config.tsx';
import { RewardsSummaryPanel } from './panels.tsx';
import { useFilteredRewardsMenuItems } from './use_filtered_rewards_menu_items.ts';

const ITEMS_COLUMN_WIDTH = 224;

/** Data-panel slot: the inner panel fills the slot so its detail rows pin to the bottom. */
const panelSlotSx = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  '& > *': { flex: 1 },
} as const;

interface RewardsPanelProps {
  open: boolean;
  onNavigate?: () => void;
}

export const RewardsPanel = memo(function RewardsPanel({ open, onNavigate }: RewardsPanelProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  // Hovered panels stay mounted (display:none) so switching back doesn't flash a skeleton.
  const [mountedKeys, setMountedKeys] = useState<Set<string>>(() => new Set());
  const menuItems = useFilteredRewardsMenuItems();

  // Reopen starts on the summary panel, not whatever was hovered last time.
  useEffect(() => {
    if (!open) setHoveredKey(null);
  }, [open]);

  useEffect(() => {
    if (!hoveredKey) return;
    setMountedKeys((prev) => {
      if (prev.has(hoveredKey)) return prev;
      const next = new Set(prev);
      next.add(hoveredKey);
      return next;
    });
  }, [hoveredKey]);

  const handleItemClick = useCallback(
    (path: (typeof menuItems)[number]['path']) => {
      onNavigate?.();
      void navigate({ to: path });
    },
    [navigate, onNavigate]
  );

  if (!menuItems.length) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5 }}>
      <Box
        sx={{
          // Fixed only when it shares the row with the data panel; on its own the column fills
          // the pane so the card has no dead space to the right (matches the Tools pane).
          width: REWARDS_MENU_DETAILS_ENABLED ? ITEMS_COLUMN_WIDTH : '100%',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {menuItems.map(({ key, path, label, description, Icon }) => (
          <NavMenuItemButton
            key={key}
            isHighlighted={hoveredKey === key}
            onMouseEnter={() => setHoveredKey(key)}
            onFocus={() => setHoveredKey(key)}
            onClick={() => handleItemClick(path)}
            tabIndex={hoverMenuItemTabIndex(open)}
            disableRipple
          >
            <Typography component="span" sx={navMenuItemHeaderSx}>
              <Icon sx={navMenuItemIconSx} />
              {t(label)}
            </Typography>
            <Typography sx={navMenuItemDescriptionSx}>{t(description)}</Typography>
          </NavMenuItemButton>
        ))}
      </Box>

      {REWARDS_MENU_DETAILS_ENABLED ? (
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ ...panelSlotSx, display: hoveredKey ? 'none' : 'flex' }}>
            <RewardsSummaryPanel />
          </Box>
          {menuItems.map(({ key, Panel }) =>
            mountedKeys.has(key) || hoveredKey === key ? (
              <Box key={key} sx={{ ...panelSlotSx, display: hoveredKey === key ? 'flex' : 'none' }}>
                <Panel />
              </Box>
            ) : null
          )}
        </Box>
      ) : null}
    </Box>
  );
});
